import React from 'react';
import { Route, Routes, BrowserRouter as Router } from 'react-router-dom';
import './App.css';

import { ConfigProvider, theme as antdTheme } from 'antd';

import { ToastMsgInit } from './components/common/ToastMsg';
import { ToastProvider, useToast } from './context/ToastContext';
import { AuthProvider } from './context/AuthContext';
import { ThemeProvider, useTheme } from './context/ThemeContext';

// Auth
import Login from './pages/auth/Login';

// Layout & route guard
import ProtectedRoute from './routes/ProtectedRoute';
import AppLayout from './components/layout/AppLayout';

// Pages
import Dashboard from './pages/dashboard/Dashboard';
import Trends from './pages/dashboard/Trends';
import FinanceIndex from './pages/finance/FinanceIndex';
import HROverview from './pages/hr/HROverview';
import SalesOverview from './pages/sales/SalesOverview';
import ProjectsOverview from './pages/projects/ProjectsOverview';
import AlertsList from './pages/alerts/AlertsList';
import UsersManager from './pages/settings/UsersManager';
import RolesManager from './pages/settings/RolesManager';
import ConnectCRM from './pages/settings/ConnectCRM';
import ConnectHRMS from './pages/settings/ConnectHRMS';
import ConnectTicketing from './pages/settings/ConnectTicketing';
import SupportOverview from './pages/support/SupportOverview';
import PeoplePage from './pages/people/PeoplePage';
import Targets from './pages/settings/Targets';
import Assignments from './pages/assignments/Assignments';
import NotFound from './pages/boundary/NotFound';

/**
 * Wrapper to initialize the global ToastMsg singleton
 * so apiClient and other non-component code can show toasts.
 */
const AppWrapper = () => {
  const { showToast } = useToast();
  ToastMsgInit(showToast);

  return (
    <Router>
      <Routes>
        {/* Public: login */}
        <Route path="/" element={<Login />} />

        {/* Protected routes — wrapped in AppLayout (sidebar + header + Outlet) */}
        <Route
          element={
            <ProtectedRoute>
              <AppLayout />
            </ProtectedRoute>
          }
        >
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/dashboard/trends" element={<Trends />} />
          <Route path="/finance" element={<FinanceIndex />} />
          <Route path="/hr" element={<HROverview />} />
          <Route path="/sales" element={<SalesOverview />} />
          <Route path="/support" element={<SupportOverview />} />
          <Route path="/people" element={<PeoplePage />} />
          <Route path="/projects" element={<ProjectsOverview />} />
          <Route path="/assignments" element={<Assignments />} />
          <Route path="/alerts" element={<AlertsList />} />
          <Route path="/settings/users" element={<UsersManager />} />
          <Route path="/settings/roles" element={<RolesManager />} />
          <Route path="/settings/connect-crm" element={<ConnectCRM />} />
          <Route path="/settings/connect-hrms" element={<ConnectHRMS />} />
          <Route path="/settings/connect-fixdesk" element={<ConnectTicketing />} />
          <Route path="/settings/targets" element={<Targets />} />
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
    </Router>
  );
};

const AntdThemeBridge = ({ children }) => {
  const { isDark } = useTheme();
  return (
    <ConfigProvider
      theme={{
        algorithm: isDark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
        token: {
          colorPrimary: '#6366f1',
          borderRadius: 8,
          fontFamily: 'Inter, sans-serif',
        },
      }}
    >
      {children}
    </ConfigProvider>
  );
};

function App() {
  return (
    <ThemeProvider>
      <AntdThemeBridge>
        <AuthProvider>
          <ToastProvider>
            <AppWrapper />
          </ToastProvider>
        </AuthProvider>
      </AntdThemeBridge>
    </ThemeProvider>
  );
}

export default App;
